import Image from 'next/image';
import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function PendleYTTriggerFullDetails({ trigger }: Props) {
  const { getNetworkChainId, getChainIconUrl } = useGetChainInfo();

  const networkName = useMemo(() => {
    // Use context (new approach)
    if (trigger.context?.network) {
      return trigger.context.network.toLowerCase();
    }

    // @deprecated - Fallback to description extraction for legacy triggers
    if (trigger.description) {
      const match = trigger.description.match(/on\s+(\w+)$/i);
      return match ? match[1].toLowerCase() : '';
    }

    return '';
  }, [trigger.context?.network, trigger.description]);

  const networkChainId = useMemo(() => {
    if (!networkName) return 1;
    return getNetworkChainId(networkName === 'hyperevm' ? 'hyperliquid-mainnet' : networkName) ?? 1;
  }, [networkName, getNetworkChainId]);

  const ytAddress = useMemo(() => {
    // Use context (new approach)
    if (trigger.context?.ytAddress) {
      return trigger.context.ytAddress;
    }

    // @deprecated - Fallback to signature extraction for legacy triggers
    if (trigger.signature) {
      const match = trigger.signature.match(/@([^@]+)$/);
      return match ? match[1] : '';
    }

    return '';
  }, [trigger.context?.ytAddress, trigger.signature]);

  const networkDisplay = useMemo(() => {
    if (!networkChainId) return '-';
    const chainIconUrl = getChainIconUrl(networkChainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkName ?? 'chain'} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkName}</span>
      </div>
    );
  }, [trigger.description, trigger.contracts, getChainIconUrl]);

  const contractDisplay = useMemo(() => {
    if (!ytAddress) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <Text as="span" variant="body-sm" color="muted">
          Snapshots of Pendle YT points calculations for
        </Text>
        <AddressChip address={ytAddress} chainId={networkChainId} />
      </div>
    );
  }, [ytAddress, trigger.contracts]);

  const volumeCurrencyDisplay = useMemo(() => {
    const currencyToUse = trigger.context?.volume_currency_expression ?? trigger.currencyVolumeExpression;
    if (!currencyToUse) return '-';
    return <CurrencyDisplay value={currencyToUse} chainId={networkChainId} />;
  }, [trigger.context?.volume_currency_expression, trigger.currencyVolumeExpression, networkChainId]);

  const conditionExpression = useMemo(() => {
    return trigger.context?.condition_expression ?? trigger.conditionExpression ?? '-';
  }, [trigger.context?.condition_expression, trigger.conditionExpression]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value={contractDisplay} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={conditionExpression} />
    </KeyValueTable>
  );
}
